import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { integrationsAPI } from '../utils/api';
import { FeatureLockedBadge, hasFeatureAccess } from './FeatureGate';
import { 
  Plug, Lock, Check, X, RefreshCw, ExternalLink, 
  ChevronRight, Zap, Settings, AlertCircle, Crown, ArrowRight,
  FileSpreadsheet, MessageSquare, Users, Mail,
  Calendar, Video, Cloud, CreditCard, Webhook, Plus, Trash2, History, CheckCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

// Integration icons mapping
const INTEGRATION_ICONS = {
  google_sheets: FileSpreadsheet,
  google_drive: Cloud,
  slack: MessageSquare,
  microsoft_teams: Users,
  mailchimp: Mail,
  google_calendar: Calendar,
  zoom: Video,
  dropbox: Cloud,
  paypal: CreditCard,
  custom_webhook: Webhook,
};

export const IntegrationsPanel = ({ formId, formFields = [], formName = 'Form' }) => {
  const { usage } = useAuth();
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [formIntegrations, setFormIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [configDialog, setConfigDialog] = useState({ open: false, integration: null });
  const [configuring, setConfiguring] = useState(false);
  
  // Slack specific
  const [channels, setChannels] = useState([]);

  const loadIntegrations = useCallback(async () => {
    try {
      const [availableRes, formRes] = await Promise.all([
        integrationsAPI.getAvailable(formId),
        integrationsAPI.getFormIntegrations(formId)
      ]);
      setIntegrations(availableRes.data);
      setFormIntegrations(formRes.data);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  const handleConnect = async (integration) => {
    if (!integration.available) {
      toast.error('This integration requires a Growth plan. Please upgrade.');
      return;
    }

    try {
      let response;
      if (integration.type === 'google_sheets') {
        response = await integrationsAPI.startGoogleSheetsOAuth(formId);
      } else if (integration.type === 'google_drive') {
        response = await integrationsAPI.startGoogleDriveOAuth(formId);
      } else if (integration.type === 'slack') {
        response = await integrationsAPI.startSlackOAuth(formId);
      } else if (integration.type === 'google_calendar') {
        response = await integrationsAPI.startGoogleCalendarOAuth(formId);
      } else if (integration.type === 'zoom') {
        response = await integrationsAPI.startZoomOAuth(formId);
      } else if (integration.type === 'dropbox') {
        response = await integrationsAPI.startDropboxOAuth(formId);
      } else if (integration.type === 'microsoft_teams' || integration.type === 'mailchimp' || integration.type === 'paypal' || integration.type === 'custom_webhook') {
        // These use API keys/webhooks, open config dialog
        setConfigDialog({ open: true, integration });
        return;
      } else {
        toast.info('This integration will be available soon!');
        return;
      }

      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error('Failed to start authentication');
    }
  };

  const handleDisconnect = async (integrationType) => {
    try {
      await integrationsAPI.disconnect(integrationType);
      toast.success('Integration disconnected');
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleConfigure = async (integration) => {
    setConfigDialog({ open: true, integration });
    
    // Load integration-specific data (only Slack needs channel listing)
    if (integration.type === 'slack' && integration.connected) {
      try {
        const res = await integrationsAPI.listSlackChannels();
        setChannels(res.data || []);
      } catch (error) {
        toast.error('Failed to load channels');
        setChannels([]);
      }
    }
  };

  const handleSaveConfig = async (integrationType, config, enabled) => {
    setConfiguring(true);
    try {
      await integrationsAPI.updateFormIntegration(formId, integrationType, {
        enabled,
        config,
        field_mapping: {}
      });
      toast.success('Integration settings saved');
      setConfigDialog({ open: false, integration: null });
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setConfiguring(false);
    }
  };

  const handleToggleIntegration = async (integration, enabled) => {
    const currentConfig = formIntegrations[integration.type]?.config || {};
    await handleSaveConfig(integration.type, currentConfig, enabled);
  };

  const handleTestIntegration = async (integrationType) => {
    try {
      const res = await integrationsAPI.test(integrationType);
      if (res.data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error(res.data.error || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const getIntegrationIcon = (type) => {
    const Icon = INTEGRATION_ICONS[type] || Plug;
    return Icon;
  };

  const isIntegrationEnabled = (type) => {
    return formIntegrations[type]?.enabled || false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const userPlan = usage?.plan || 'free';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Integrations</h3>
        <p className="text-sm text-slate-600">
          Connect your forms to third-party apps and automate your workflow
        </p>
      </div>

      {/* Plan upgrade notice for free users */}
      {userPlan === 'free' && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-amber-900">Unlock all integrations</p>
                  <p className="text-sm text-amber-700">Upgrade to Growth for Slack, Teams, Zoom, and more</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                Upgrade
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integrations list */}
      <div className="grid gap-4">
        {integrations.map((integration) => {
          const Icon = getIntegrationIcon(integration.type);
          const isEnabled = isIntegrationEnabled(integration.type);
          const isAvailable = integration.available;

          return (
            <Card 
              key={integration.type} 
              className={`${!isAvailable ? 'opacity-60' : ''}`}
              data-testid={`integration-${integration.type}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      integration.connected ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${integration.connected ? 'text-green-600' : 'text-slate-600'}`} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium">{integration.name}</h4>
                        {!isAvailable && (
                          <FeatureLockedBadge requiredPlan="growth" />
                        )}
                        {integration.connected && (
                          <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                            <Check className="w-3 h-3 mr-1" />
                            Connected
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{integration.description}</p>
                      {integration.connection_info?.email && (
                        <p className="text-xs text-slate-500 mt-1">
                          Connected as: {integration.connection_info.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {integration.connected ? (
                      <>
                        <div className="flex items-center space-x-2">
                          <Label htmlFor={`enable-${integration.type}`} className="text-sm">
                            Enable
                          </Label>
                          <Switch
                            id={`enable-${integration.type}`}
                            checked={isEnabled}
                            onCheckedChange={(checked) => handleToggleIntegration(integration, checked)}
                            disabled={!isAvailable}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleConfigure(integration)}
                          data-testid={`configure-${integration.type}`}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Configure
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect(integration.type)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      isAvailable ? (
                        <Button
                          onClick={() => handleConnect(integration)}
                          data-testid={`connect-${integration.type}`}
                        >
                          <Plug className="w-4 h-4 mr-2" />
                          Connect
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate('/pricing')}
                          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                          data-testid={`upgrade-${integration.type}`}
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Upgrade
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Configuration Dialog */}
      <Dialog open={configDialog.open} onOpenChange={(open) => setConfigDialog({ open, integration: configDialog.integration })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              {configDialog.integration && (
                <>
                  {React.createElement(getIntegrationIcon(configDialog.integration.type), { className: "w-5 h-5" })}
                  <span>Configure {configDialog.integration?.name}</span>
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Set up how this integration works with your form
            </DialogDescription>
          </DialogHeader>

          {configDialog.integration?.type === 'google_sheets' && (
            <GoogleSheetsConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
              formName={formName}
            />
          )}

          {configDialog.integration?.type === 'google_drive' && (
            <GoogleDriveConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'slack' && (
            <SlackConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              channels={channels}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'microsoft_teams' && (
            <TeamsConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'mailchimp' && (
            <MailchimpConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'google_calendar' && (
            <GoogleCalendarConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'zoom' && (
            <ZoomConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'dropbox' && (
            <DropboxConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'paypal' && (
            <PayPalConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              onSave={handleSaveConfig}
              onTest={handleTestIntegration}
              configuring={configuring}
            />
          )}

          {configDialog.integration?.type === 'custom_webhook' && (
            <CustomWebhookConfig
              integration={configDialog.integration}
              formIntegrations={formIntegrations}
              formId={formId}
              onSave={handleSaveConfig}
              configuring={configuring}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Google Sheets Configuration Component
// Simplified to comply with Google's minimum scope policy (drive.file only)
const GoogleSheetsConfig = ({ integration, formIntegrations, onSave, onTest, configuring, formFields = [], formName = 'Form' }) => {
  const currentConfig = formIntegrations.google_sheets?.config || {};
  const [sheetName, setSheetName] = useState(currentConfig.sheet_name || 'Responses');
  const [enabled, setEnabled] = useState(formIntegrations.google_sheets?.enabled || false);
  
  // Check if spreadsheet was already created
  const spreadsheetId = currentConfig.spreadsheet_id;
  const spreadsheetCreated = !!spreadsheetId;

  const handleSave = () => {
    const config = {
      mode: 'auto_create',
      sheet_name: sheetName || 'Responses',
      new_spreadsheet_name: formName,
      create_new_spreadsheet: !spreadsheetCreated,
    };

    if (spreadsheetId) {
      config.spreadsheet_id = spreadsheetId;
    }

    onSave('google_sheets', config, enabled);
  };

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FileSpreadsheet className="h-5 w-5 text-indigo-600 mt-0.5" />
          <div>
            <p className="font-medium text-indigo-900">Automatic Spreadsheet Creation</p>
            <p className="text-sm text-indigo-700 mt-1">
              A Google Sheet named "<strong>{formName}</strong>" will be automatically created when the first submission is received.
            </p>
          </div>
        </div>
      </div>

      {/* Show existing spreadsheet info if already created */}
      {spreadsheetCreated && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-green-900">Spreadsheet Active</p>
              <p className="text-sm text-green-700 mt-1">
                Your submissions are being recorded to the linked Google Sheet.
              </p>
              <a 
                href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-green-700 hover:text-green-800 mt-2 font-medium"
              >
                Open Spreadsheet <ExternalLink className="h-3.5 w-3.5 ml-1" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Sheet Tab Name */}
      <div className="space-y-2">
        <Label>Sheet Tab Name</Label>
        <Input 
          value={sheetName}
          onChange={(e) => setSheetName(e.target.value)}
          placeholder="Responses"
          disabled={spreadsheetCreated}
        />
        <p className="text-xs text-slate-500">
          {spreadsheetCreated 
            ? 'Sheet tab name cannot be changed after creation'
            : 'Name of the sheet tab where submissions will be recorded'
          }
        </p>
      </div>

      {/* How it works */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
        <p className="text-sm text-slate-700">
          <strong>How it works:</strong> When enabled, each form submission will automatically:
        </p>
        <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
          <li>Create a Google Sheet (on first submission) named after your form</li>
          <li>Add your form field labels as column headers</li>
          <li>Append each submission as a new row with a timestamp</li>
        </ul>
      </div>

      <div className="flex items-center space-x-2 pt-2">
        <Switch id="enable-sheets" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-sheets">Enable on form submission</Label>
      </div>

      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSave}
          disabled={configuring}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Slack Configuration Component
const SlackConfig = ({ integration, formIntegrations, channels, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.slack?.config || {};
  const [channelId, setChannelId] = useState(currentConfig.channel_id || '');
  const [enabled, setEnabled] = useState(formIntegrations.slack?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Select Channel</Label>
        <Select value={channelId} onValueChange={setChannelId}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a channel" />
          </SelectTrigger>
          <SelectContent>
            {channels.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.is_private ? '🔒 ' : '#'}{c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-slack" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-slack">Enable on form submission</Label>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('slack')}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('slack', { channel_id: channelId }, enabled)}
          disabled={configuring || !channelId}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Microsoft Teams Configuration Component
const TeamsConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.microsoft_teams?.config || {};
  const [webhookUrl, setWebhookUrl] = useState(currentConfig.webhook_url || '');
  const [enabled, setEnabled] = useState(formIntegrations.microsoft_teams?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Webhook URL</Label>
        <Input 
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://outlook.office.com/webhook/..."
        />
        <p className="text-xs text-slate-500">
          <a href="https://docs.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
            Learn how to create a Teams webhook
          </a>
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-teams" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-teams">Enable on form submission</Label>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('microsoft_teams')} disabled={!webhookUrl}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('microsoft_teams', { webhook_url: webhookUrl }, enabled)}
          disabled={configuring || !webhookUrl}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Mailchimp Configuration Component
const MailchimpConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.mailchimp?.config || {};
  const [apiKey, setApiKey] = useState(currentConfig.api_key || '');
  const [audienceId, setAudienceId] = useState(currentConfig.audience_id || '');
  const [enabled, setEnabled] = useState(formIntegrations.mailchimp?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>API Key</Label>
        <Input 
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="xxxxxxxx-us1"
        />
        <p className="text-xs text-slate-500">
          <a href="https://mailchimp.com/help/about-api-keys/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
            Get your Mailchimp API key
          </a>
        </p>
      </div>

      <div className="space-y-2">
        <Label>Audience ID (List ID)</Label>
        <Input 
          value={audienceId}
          onChange={(e) => setAudienceId(e.target.value)}
          placeholder="abc123def4"
        />
        <p className="text-xs text-slate-500">
          Find this in Mailchimp: Audience → Settings → Audience name and defaults
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-mailchimp" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-mailchimp">Enable on form submission</Label>
      </div>

      <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
        <strong>Field Mapping:</strong> Form email field will be added as subscriber. Include fields named "first_name" or "last_name" to populate merge fields.
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('mailchimp')} disabled={!apiKey}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('mailchimp', { api_key: apiKey, audience_id: audienceId }, enabled)}
          disabled={configuring || !apiKey || !audienceId}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Google Calendar Configuration Component
const GoogleCalendarConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.google_calendar?.config || {};
  const [calendarId, setCalendarId] = useState(currentConfig.calendar_id || 'primary');
  const [durationHours, setDurationHours] = useState(currentConfig.duration_hours || '1');
  const [timezone, setTimezone] = useState(currentConfig.timezone || 'UTC');
  const [enabled, setEnabled] = useState(formIntegrations.google_calendar?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Calendar</Label>
        <Select value={calendarId} onValueChange={setCalendarId}>
          <SelectTrigger>
            <SelectValue placeholder="Select calendar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="primary">Primary Calendar</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Default Event Duration (hours)</Label>
        <Select value={durationHours} onValueChange={setDurationHours}>
          <SelectTrigger>
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0.5">30 minutes</SelectItem>
            <SelectItem value="1">1 hour</SelectItem>
            <SelectItem value="1.5">1.5 hours</SelectItem>
            <SelectItem value="2">2 hours</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Timezone</Label>
        <Select value={timezone} onValueChange={setTimezone}>
          <SelectTrigger>
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="UTC">UTC</SelectItem>
            <SelectItem value="America/New_York">Eastern Time</SelectItem>
            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
            <SelectItem value="Europe/London">London</SelectItem>
            <SelectItem value="Asia/Kolkata">India</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-gcal" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-gcal">Enable on form submission</Label>
      </div>

      <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
        <strong>Field Mapping:</strong> Include fields named "date", "time", "title", "description", "email" (for attendee) to auto-populate event details.
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('google_calendar')}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('google_calendar', { 
            calendar_id: calendarId, 
            duration_hours: durationHours,
            timezone 
          }, enabled)}
          disabled={configuring}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Zoom Configuration Component
const ZoomConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.zoom?.config || {};
  const [durationMinutes, setDurationMinutes] = useState(currentConfig.duration_minutes || '60');
  const [timezone, setTimezone] = useState(currentConfig.timezone || 'UTC');
  const [waitingRoom, setWaitingRoom] = useState(currentConfig.waiting_room !== false);
  const [joinBeforeHost, setJoinBeforeHost] = useState(currentConfig.join_before_host || false);
  const [enabled, setEnabled] = useState(formIntegrations.zoom?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Default Meeting Duration</Label>
        <Select value={durationMinutes} onValueChange={setDurationMinutes}>
          <SelectTrigger>
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="60">1 hour</SelectItem>
            <SelectItem value="90">1.5 hours</SelectItem>
            <SelectItem value="120">2 hours</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Timezone</Label>
        <Select value={timezone} onValueChange={setTimezone}>
          <SelectTrigger>
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="UTC">UTC</SelectItem>
            <SelectItem value="America/New_York">Eastern Time</SelectItem>
            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
            <SelectItem value="Europe/London">London</SelectItem>
            <SelectItem value="Asia/Kolkata">India</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between py-2">
        <Label>Enable waiting room</Label>
        <Switch checked={waitingRoom} onCheckedChange={setWaitingRoom} />
      </div>

      <div className="flex items-center justify-between py-2">
        <Label>Allow join before host</Label>
        <Switch checked={joinBeforeHost} onCheckedChange={setJoinBeforeHost} />
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-zoom" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-zoom">Enable on form submission</Label>
      </div>

      <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
        <strong>Field Mapping:</strong> Include fields named "date", "time", "topic", "description" to auto-populate meeting details.
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('zoom')}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('zoom', { 
            duration_minutes: durationMinutes,
            timezone,
            waiting_room: waitingRoom,
            join_before_host: joinBeforeHost
          }, enabled)}
          disabled={configuring}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Dropbox Configuration Component
// Google Drive Configuration Component
const GoogleDriveConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.google_drive?.config || {};
  const [folderName, setFolderName] = useState(currentConfig.folder_name || 'Formlio Uploads');
  const [enabled, setEnabled] = useState(formIntegrations.google_drive?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Google Drive Folder Name</Label>
        <Input 
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          placeholder="Formlio Uploads"
        />
        <p className="text-xs text-slate-500">
          File uploads from submissions will be saved to this folder in your Google Drive
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-drive" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-drive">Enable file upload to Google Drive</Label>
      </div>

      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-700">
        <strong>How it works:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>When users submit forms with file uploads, files are automatically saved to your Google Drive</li>
          <li>A folder named "{folderName}" will be created if it doesn't exist</li>
          <li>File links are stored with submission data for easy access</li>
        </ul>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('google_drive')}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('google_drive', { folder_name: folderName }, enabled)}
          disabled={configuring}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Dropbox Configuration Component
const DropboxConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.dropbox?.config || {};
  const [folderPath, setFolderPath] = useState(currentConfig.folder_path || '/Formlio');
  const [enabled, setEnabled] = useState(formIntegrations.dropbox?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Destination Folder</Label>
        <Input 
          value={folderPath}
          onChange={(e) => setFolderPath(e.target.value)}
          placeholder="/Formlio"
        />
        <p className="text-xs text-slate-500">
          Submissions will be saved as JSON files in this folder
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-dropbox" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-dropbox">Enable on form submission</Label>
      </div>

      <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
        Each submission creates a JSON file with all form data, timestamped with submission date.
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('dropbox')}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('dropbox', { folder_path: folderPath }, enabled)}
          disabled={configuring}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// PayPal Configuration Component
const PayPalConfig = ({ integration, formIntegrations, onSave, onTest, configuring }) => {
  const currentConfig = formIntegrations.paypal?.config || {};
  const [clientId, setClientId] = useState(currentConfig.client_id || '');
  const [clientSecret, setClientSecret] = useState(currentConfig.client_secret || '');
  const [sandbox, setSandbox] = useState(currentConfig.sandbox !== false);
  const [defaultAmount, setDefaultAmount] = useState(currentConfig.default_amount || '10.00');
  const [currency, setCurrency] = useState(currentConfig.currency || 'USD');
  const [enabled, setEnabled] = useState(formIntegrations.paypal?.enabled || false);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Client ID</Label>
        <Input 
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder="Your PayPal Client ID"
        />
      </div>

      <div className="space-y-2">
        <Label>Client Secret</Label>
        <Input 
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder="Your PayPal Client Secret"
        />
        <p className="text-xs text-slate-500">
          <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
            Get your PayPal API credentials
          </a>
        </p>
      </div>

      <div className="flex items-center justify-between py-2">
        <div>
          <Label>Sandbox Mode</Label>
          <p className="text-xs text-slate-500">Use PayPal sandbox for testing</p>
        </div>
        <Switch checked={sandbox} onCheckedChange={setSandbox} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Default Amount</Label>
          <Input 
            value={defaultAmount}
            onChange={(e) => setDefaultAmount(e.target.value)}
            placeholder="10.00"
          />
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger>
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="INR">INR</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch id="enable-paypal" checked={enabled} onCheckedChange={setEnabled} />
        <Label htmlFor="enable-paypal">Enable on form submission</Label>
      </div>

      <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
        <strong>Field Mapping:</strong> Include a field named "amount" to set dynamic payment amounts.
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => onTest('paypal')} disabled={!clientId || !clientSecret}>
          Test Connection
        </Button>
        <Button 
          onClick={() => onSave('paypal', { 
            client_id: clientId,
            client_secret: clientSecret,
            sandbox,
            default_amount: defaultAmount,
            currency
          }, enabled)}
          disabled={configuring || !clientId || !clientSecret}
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

// Custom Webhook Configuration Component
const CustomWebhookConfig = ({ integration, formIntegrations, formId, onSave, configuring }) => {
  const currentConfig = formIntegrations.custom_webhook?.config || {};
  const [webhookUrl, setWebhookUrl] = useState(currentConfig.webhook_url || '');
  const [httpMethod, setHttpMethod] = useState(currentConfig.http_method || 'POST');
  const [secretKey, setSecretKey] = useState(currentConfig.secret_key || '');
  const [enabled, setEnabled] = useState(formIntegrations.custom_webhook?.enabled || false);
  const [customHeaders, setCustomHeaders] = useState(
    currentConfig.custom_headers && typeof currentConfig.custom_headers === 'object'
      ? Object.entries(currentConfig.custom_headers).map(([key, value]) => ({ key, value }))
      : []
  );
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const addHeader = () => {
    setCustomHeaders([...customHeaders, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    setCustomHeaders(customHeaders.filter((_, i) => i !== index));
  };

  const updateHeader = (index, field, value) => {
    const updated = [...customHeaders];
    updated[index][field] = value;
    setCustomHeaders(updated);
  };

  const handleTestWebhook = async () => {
    if (!webhookUrl) {
      setTestResult({ success: false, error: 'Please enter a webhook URL' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      // First save the config temporarily
      const headersObj = customHeaders.reduce((acc, h) => {
        if (h.key && h.value) acc[h.key] = h.value;
        return acc;
      }, {});

      await onSave('custom_webhook', {
        webhook_url: webhookUrl,
        http_method: httpMethod,
        secret_key: secretKey,
        custom_headers: headersObj
      }, enabled);

      // Then test
      const response = await fetch(`${API_BASE}/api/forms/${formId}/webhook/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      setTestResult({ success: false, error: error.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const loadWebhookLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await fetch(`${API_BASE}/api/forms/${formId}/webhook-logs?limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setLogs(data || []);
    } catch (error) {
      console.error('Failed to load webhook logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleShowLogs = () => {
    setShowLogs(!showLogs);
    if (!showLogs) {
      loadWebhookLogs();
    }
  };

  const handleSave = () => {
    const headersObj = customHeaders.reduce((acc, h) => {
      if (h.key && h.value) acc[h.key] = h.value;
      return acc;
    }, {});

    onSave('custom_webhook', {
      webhook_url: webhookUrl,
      http_method: httpMethod,
      secret_key: secretKey,
      custom_headers: headersObj
    }, enabled);
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Webhook URL */}
      <div className="space-y-2">
        <Label className="font-medium">Webhook URL <span className="text-red-500">*</span></Label>
        <Input 
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://example.com/webhook"
          data-testid="webhook-url-input"
        />
        <p className="text-xs text-slate-500">
          Enter the URL where form data will be sent on submission
        </p>
      </div>

      {/* HTTP Method */}
      <div className="space-y-2">
        <Label className="font-medium">HTTP Method</Label>
        <Select value={httpMethod} onValueChange={setHttpMethod}>
          <SelectTrigger data-testid="webhook-method-select">
            <SelectValue placeholder="Select method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="PUT">PUT</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Secret Key (Optional) */}
      <div className="space-y-2">
        <Label className="font-medium">Secret Key (Optional)</Label>
        <Input 
          type="password"
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          placeholder="Your secret key for signature verification"
          data-testid="webhook-secret-input"
        />
        <p className="text-xs text-slate-500">
          If provided, requests will include an <code className="bg-slate-100 px-1 rounded">X-Formlio-Signature</code> header with HMAC SHA256 signature
        </p>
      </div>

      {/* Custom Headers */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="font-medium">Custom Headers (Optional)</Label>
          <Button variant="outline" size="sm" onClick={addHeader} data-testid="add-header-btn">
            <Plus className="w-3 h-3 mr-1" />
            Add Header
          </Button>
        </div>
        
        {customHeaders.length > 0 && (
          <div className="space-y-2">
            {customHeaders.map((header, index) => (
              <div key={index} className="flex gap-2 items-center">
                <Input
                  className="flex-1"
                  value={header.key}
                  onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  placeholder="Header name"
                />
                <Input
                  className="flex-1"
                  value={header.value}
                  onChange={(e) => updateHeader(index, 'value', e.target.value)}
                  placeholder="Header value"
                />
                <Button variant="ghost" size="icon" onClick={() => removeHeader(index)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enable Toggle */}
      <div className="flex items-center space-x-2 py-2">
        <Switch id="enable-webhook" checked={enabled} onCheckedChange={setEnabled} data-testid="webhook-enable-toggle" />
        <Label htmlFor="enable-webhook">Send webhook on form submission</Label>
      </div>

      {/* Payload Preview */}
      <div className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono overflow-x-auto">
        <p className="text-slate-400 mb-2">// Webhook payload structure:</p>
        <pre>{`{
  "event": "form.submitted",
  "timestamp": "2024-01-15T10:30:00Z",
  "form": {
    "id": "form_abc123",
    "name": "Contact Form"
  },
  "submission": {
    "id": "sub_xyz789",
    "submitted_at": "2024-01-15T10:30:00Z"
  },
  "data": {
    "name": { "label": "Name", "type": "text", "value": "John" },
    "email": { "label": "Email", "type": "email", "value": "john@example.com" }
  }
}`}</pre>
      </div>

      {/* Integration Tips */}
      <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg">
        <p className="text-sm font-medium text-blue-800 mb-2">Pro Tips</p>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• Connect to <strong>Zapier</strong>: Use Webhooks by Zapier trigger</li>
          <li>• Connect to <strong>Make.com</strong>: Use Custom Webhook module</li>
          <li>• Connect to <strong>n8n</strong>: Use Webhook node</li>
          <li>• Connect to your own system with any URL that accepts JSON POST</li>
        </ul>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-2">
            {testResult.success ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <X className="w-4 h-4 text-red-600" />
            )}
            <span className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {testResult.success ? 'Test successful!' : 'Test failed'}
            </span>
          </div>
          {testResult.message && <p className="text-xs text-green-700 mt-1">{testResult.message}</p>}
          {testResult.error && <p className="text-xs text-red-700 mt-1">{testResult.error}</p>}
          {testResult.status_code && <p className="text-xs mt-1">Status code: {testResult.status_code}</p>}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleTestWebhook} disabled={testing || !webhookUrl} data-testid="test-webhook-btn">
            {testing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
            {testing ? 'Testing...' : 'Test Webhook'}
          </Button>
          <Button variant="outline" onClick={handleShowLogs} data-testid="view-logs-btn">
            <History className="w-4 h-4 mr-1" />
            Logs
          </Button>
        </div>
        <Button 
          onClick={handleSave}
          disabled={configuring || !webhookUrl}
          data-testid="save-webhook-btn"
        >
          {configuring ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Webhook Logs Section */}
      {showLogs && (
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm">Webhook Delivery Logs</h4>
            <Button variant="ghost" size="sm" onClick={loadWebhookLogs} disabled={loadingLogs}>
              <RefreshCw className={`w-3 h-3 mr-1 ${loadingLogs ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {loadingLogs ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No webhook logs yet. Submit a form to see delivery logs.
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {logs.map((log, index) => (
                <div 
                  key={log.id || index}
                  className={`p-3 rounded-lg text-xs ${
                    log.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium ${log.success ? 'text-green-700' : 'text-red-700'}`}>
                      {log.success ? '✓ Success' : '✗ Failed'}
                    </span>
                    <span className="text-slate-500">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <span>Status: {log.status_code || 'N/A'}</span>
                    <span>Attempt: {log.attempt || 1}</span>
                    {log.error && <span className="text-red-600">Error: {log.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
